// WPS-020 · Public Smart Card landing · wunli.com/sc/:slug
// Visitors land here when they scan a QR or tap an NFC chip.
// Mobile-first · Executive Light · Add-to-Contacts CTA.
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { smartCardApi, referralApi } from '../../services/api';
import { Download, Mail, Phone, Globe, MapPin, ExternalLink, Briefcase } from 'lucide-react';

const SmartCardPublic = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const r = await smartCardApi.getPublic(slug);
      if (!r?.success) setError(r?.message || 'Smart Card not found');
      else setProfile(r.data);
      setLoading(false);

      // WPS-027 · Track referral if ?ref present (or default to slug = own page)
      const refSlug = searchParams.get('ref') || slug;
      if (refSlug) {
        const existing = localStorage.getItem('wunli_ref_token');
        const tr = await referralApi.track(refSlug, existing);
        if (tr?.success && tr.data?.sessionToken) {
          localStorage.setItem('wunli_ref_token', tr.data.sessionToken);
          localStorage.setItem('wunli_ref_slug', refSlug);
        }
      }
    })();
  }, [slug, searchParams]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', color: '#64748b', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          Loading Smart Card…
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0a', color: '#ffffff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ fontSize: '13px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.2em', color: '#c9a961', marginBottom: '12px' }}>
          WUNLI · SMART CARD
        </div>
        <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>Card not found</div>
        <div style={{ fontSize: '13px', color: '#94a3b8' }}>This Smart Card slug may be inactive or rotated.</div>
        <a href="https://wunli.com" style={{ marginTop: '24px', fontSize: '12px', color: '#c9a961', textDecoration: 'none', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          Visit wunli.com →
        </a>
      </div>
    );
  }

  const card = profile.card || {};
  const vcardUrl = smartCardApi.vcardUrl(slug);
  const accent = (card.fontPairing?.display?.family) ? 'serif' : 'sans-serif';

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', paddingBottom: '80px' }}>
      {/* Header noir hero */}
      <div style={{
        background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 100%)',
        color: '#ffffff',
        padding: '32px 20px 100px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 16, right: 20, fontFamily: 'JetBrains Mono, monospace', fontSize: '9px', letterSpacing: '0.2em', color: '#c9a961' }}>
          WUNLI · SMART CARD
        </div>
        <div style={{ maxWidth: '440px', margin: '0 auto' }}>
          <div style={{
            width: '88px', height: '88px', borderRadius: '50%',
            background: '#c9a961',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '24px auto 16px',
            fontSize: '32px', fontWeight: 800, color: '#0a0a0a',
            fontFamily: 'Inter, sans-serif',
          }}>
            {(profile.fullName || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
          </div>
          <div style={{ textAlign: 'center', fontSize: '24px', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '4px' }}>
            {profile.fullName}
          </div>
          {profile.jobTitle && (
            <div style={{ textAlign: 'center', fontSize: '13px', color: '#c9a961', marginBottom: '2px' }}>
              {profile.jobTitle}
            </div>
          )}
          {profile.companyName && (
            <div style={{ textAlign: 'center', fontSize: '14px', color: '#cbd5e1', fontWeight: 500 }}>
              {profile.companyName}
            </div>
          )}
        </div>
      </div>

      {/* Floating Add-to-Contacts CTA */}
      <div style={{ maxWidth: '440px', margin: '-60px auto 0', padding: '0 20px' }}>
        <a
          href={vcardUrl}
          download
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            background: '#01579b', color: '#ffffff',
            padding: '18px 24px',
            fontSize: '15px', fontWeight: 700,
            textDecoration: 'none',
            boxShadow: '0 10px 30px rgba(1,87,155,0.4)',
            letterSpacing: '0.02em',
          }}
        >
          <Download className="h-5 w-5" />
          Add to Contacts
        </a>
        <div style={{ textAlign: 'center', fontSize: '10px', fontFamily: 'JetBrains Mono, monospace', color: '#94a3b8', marginTop: '8px', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          vCard · iOS · Android · Outlook
        </div>
      </div>

      {/* Contact rows */}
      <div style={{ maxWidth: '440px', margin: '24px auto 0', padding: '0 20px' }}>
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0' }}>
          {profile.phone && (
            <ContactRow icon={<Phone className="h-4 w-4" />} label="Phone" value={profile.phone} href={`tel:${profile.phone}`} />
          )}
          {profile.email && (
            <ContactRow icon={<Mail className="h-4 w-4" />} label="Email" value={profile.email} href={`mailto:${profile.email}`} />
          )}
          {profile.website && (
            <ContactRow icon={<Globe className="h-4 w-4" />} label="Website" value={profile.website.replace(/^https?:\/\//, '')} href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`} external />
          )}
          {profile.address && (
            <ContactRow icon={<MapPin className="h-4 w-4" />} label="Address" value={profile.address} href={`https://maps.google.com/?q=${encodeURIComponent(profile.address)}`} external />
          )}
          {profile.industry && (
            <ContactRow icon={<Briefcase className="h-4 w-4" />} label="Industry" value={profile.industry.replace(/-/g, ' ')} />
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ maxWidth: '440px', margin: '32px auto 0', padding: '0 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '10px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '8px' }}>
          Powered by Wunli Print Studio
        </div>
        <a href="https://wunli.com" style={{ fontSize: '11px', color: '#01579b', textDecoration: 'none', fontWeight: 600 }}>
          Get your Smart Card → wunli.com
        </a>
      </div>
    </div>
  );
};

const ContactRow = ({ icon, label, value, href, external }) => (
  <a
    href={href || '#'}
    target={external ? '_blank' : undefined}
    rel={external ? 'noopener noreferrer' : undefined}
    style={{
      display: 'flex', alignItems: 'center', gap: '14px',
      padding: '16px 18px',
      borderBottom: '1px solid #f1f5f9',
      textDecoration: 'none',
      color: '#0f172a',
    }}
  >
    <div style={{ width: '36px', height: '36px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#01579b' }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '10px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '2px' }}>
        {label}
      </div>
      <div style={{ fontSize: '14px', fontWeight: 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </div>
    </div>
    {external && <ExternalLink className="h-3 w-3" style={{ color: '#94a3b8' }} />}
  </a>
);

export default SmartCardPublic;
